// Modified ValidateAccount component

import { CircularProgress, Grid, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import { Box } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import useServices from '../../services/useServices';
import Fingerprint from 'fingerprintjs';
import ARSSecurityPage from './ARSSecurityPage';
import MFASecurityPage from './MFASecurityPage';
import { setSessionData, updateManifestFile, setGPSAccuracySetting } from '../../utils/utils';
import { validateEmailForMFA, sendOTP, verifyOTP } from '../../services/mfaService';

const ValidateAccount = () => {
    let { accountId, uniqueCode } = useParams();
    updateManifestFile(accountId, uniqueCode);

    const { getValidateAccount, get, put } = useServices()
    const navigate = useNavigate()
    const [isValid, setIsValid] = useState(true)
    const [loading, setLoading] = useState(true)
    const [alertOpen, setAlertOpen] = useState(false)
    const [alertMsg, setAlertMsg] = useState('')
    const [accountName, setAccountName] = useState('')
    const [requiresMFA, setRequiresMFA] = useState(false)
    const [mfaLoading, setMFALoading] = useState(false)

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertOpen(false);
    };

    const validateAccount = async () => {
        // Cache-bust: ensures the service worker / browser never serves a stale
        // validateAccLink response (which was making a bad-link followed by a
        // good-link stay in the "invalid" state until a hard refresh).
        const cacheBust = `?_=${Date.now()}`;
        try {
            // First attempt
            const resp = await getValidateAccount(`account/validateAccLink/${accountId}/${uniqueCode}${cacheBust}`);
            handleValidationResponse(resp);
        } catch (error) {
            if (error.response?.status === 417) {
                console.log('Received 417 status, attempting retry...');
                try {
                    // Explicit second attempt (fresh cache-bust so the retry isn't served from cache either)
                    const retryResp = await getValidateAccount(`account/validateAccLink/${accountId}/${uniqueCode}?_=${Date.now()}`);
                    handleValidationResponse(retryResp);
                } catch (retryError) {
                    console.log('Retry failed:', retryError);
                    handleValidationError();
                }
            } else {
                console.log('Initial attempt failed:', error);
                handleValidationError();
            }
        }
    };

    const handleValidationResponse = (resp) => {
        setSessionData(accountId, 'userID');
        setSessionData(uniqueCode, 'token');
        setSessionData(resp.data.resp, 'userData');

        // Set account name from response
        if (resp.data.resp && resp.data.resp.accountName) {
            setAccountName(resp.data.resp.accountName);
        }

        // **NEW: Store GPS Accuracy Setting from API response**
        if (resp.data.resp && resp.data.resp.gpsAccuracySetting) {
            console.log('GPS Accuracy Setting received:', resp.data.resp.gpsAccuracySetting);
            setGPSAccuracySetting(resp.data.resp.gpsAccuracySetting);
        } else {
            // Default to 'Off' if not provided
            console.log('No GPS Accuracy Setting in response, defaulting to Off');
            setGPSAccuracySetting('Off');
        }

        // Check if MFA is required
        if (resp.data.resp && resp.data.resp.requiresMFA) {
            setRequiresMFA(true);
            setLoading(false);
        } else if (resp.data.resp && typeof resp.data.resp.isPassphraseEnabled !== 'undefined') {
            setLoading(resp.data.resp.isPassphraseEnabled);
            // For accounts that do NOT require a passphrase, fetch the ARS JWT now so
            // subsequent /ars/** calls have a valid Authorization header. Without this,
            // passphrase-disabled accounts would 401 on every ARS request after the
            // Phase 5 client migration. Also covers the "mid-session officer across a
            // PWA update" bootstrap case: if validateAccLink succeeds and ars_token is
            // missing, we always (re-)issue one.
            if (!resp.data.resp.isPassphraseEnabled || !localStorage.getItem('ars_token')) {
                fetchArsTokenForLink();
            }
        } else {
            setLoading(false);
            if (!localStorage.getItem('ars_token')) {
                fetchArsTokenForLink();
            }
        }
    };

    /**
     * @param afterMfa true if called post-MFA-OTP-success → server honors the
     *                 account's Token Duration setting (up to 1 month / "Forever").
     *                 Default false = plain-link / passphrase-disabled / bootstrap
     *                 → server forces 24h regardless of admin setting (weakest auth tier).
     */
    const fetchArsTokenForLink = async (afterMfa = false, otpProof = null) => {
        try {
            const qs = afterMfa
                ? `?afterMfa=true${otpProof ? `&otpProof=${encodeURIComponent(otpProof)}` : ''}`
                : '';
            const url = `/ars/directCall/${accountId}/${uniqueCode}/issueToken${qs}`;
            const tokResp = await get(url);
            if (tokResp.status === 200 && tokResp.data && tokResp.data.access_token) {
                const expiresInSec = Number(tokResp.data.expires_in) || 86400;
                const now = Date.now();
                try {
                    localStorage.setItem('ars_token', tokResp.data.access_token);
                    localStorage.setItem('ars_token_expires_at', String(now + expiresInSec * 1000));
                    localStorage.setItem('ars_token_duration_sec', String(expiresInSec));
                } catch (e) {
                    console.log('ars_token persistence failed:', e);
                }
            }
        } catch (e) {
            // Non-fatal: if issueToken fails here, subsequent ARS calls will 401 and the
            // user will be redirected to re-validate via the 401 handler in useServices.js.
            console.log('fetchArsTokenForLink failed:', e);
        }
    };

    const handleValidationError = () => {
        // Clear any stale session state from a prior (possibly successful) link
        // so the next paste of a good link doesn't get stuck behind residual
        // localStorage data. Does NOT affect the success path — those keys are
        // written fresh in handleValidationResponse / fetchArsTokenForLink.
        try {
            ['userID', 'token', 'userData', 'ars_token', 'ars_token_expires_at', 'ars_token_duration_sec']
                .forEach(k => localStorage.removeItem(k));
        } catch (e) {
            // non-fatal; continue to show the error UI either way
        }
        setIsValid(false);
        setLoading(false);
    };

    const validatePassphrase = async (data) => {
        try {
            // New /ars/** endpoint: on valid passphrase, response body is an OAuth2-shaped
            // JSON { access_token, token_type, expires_in, scope }. We store the token
            // and its absolute expiry so every subsequent ARS call can send it in the
            // Authorization header (see useServices.js) and can detect near-expiry to
            // force a re-validate (Greg's "don't let it expire mid-scene" concern).
            const resp = await get(`/ars/directCall/${accountId}/${data.passphrase||'test'}/validatePassphrase`);
            if (resp.status === 200 && resp.data && resp.data.access_token) {
                const expiresInSec = Number(resp.data.expires_in) || 86400; // fallback 24h
                const now = Date.now();
                try {
                    localStorage.setItem('ars_token', resp.data.access_token);
                    localStorage.setItem('ars_token_expires_at', String(now + expiresInSec * 1000));
                    localStorage.setItem('ars_token_duration_sec', String(expiresInSec));
                } catch (e) {
                    // If localStorage write fails, continue — the call flow will still work
                    // because this session already authenticated; next reload will re-validate.
                    console.log('ars_token persistence failed:', e);
                }
                setSessionData(data.id, 'securityID');
                navigate('/newrequest');
            } else {
                setAlertMsg('Passphrase validation failed!');
                setAlertOpen(true);
            }
        } catch (error) {
            console.log(error);
            setAlertMsg('Passphrase validation failed!');
            setAlertOpen(true);
        }
    };

    // MFA Email Verification
    const handleVerifyEmail = async (email) => {
        setMFALoading(true);
        try {
            // First validate email
            const validateResp = await validateEmailForMFA(email, accountId);
            
            if (validateResp.status === 'SUCCESS') {
                // Send OTP
                const otpResp = await sendOTP(email, accountId);
                
                if (otpResp.status === 'SUCCESS') {
                    setMFALoading(false);
                    return true;
                } else {
                    throw new Error(otpResp.message || 'Failed to send verification code');
                }
            } else {
                throw new Error(validateResp.message || 'Email domain not allowed');
            }
        } catch (error) {
            setMFALoading(false);
            setAlertMsg(error.message || 'Failed to send verification code');
            setAlertOpen(true);
            return false;
        }
    };

    // MFA OTP Verification
    const handleVerifyOTP = async (email, otp) => {
        setMFALoading(true);
        try {
            const resp = await verifyOTP(email, otp, accountId);
            
            if (resp.status === 'SUCCESS') {
                // Store username (email without domain) as security ID
                const username = email.substring(0, email.indexOf('@'));
                setSessionData(username, 'securityID');

                // Fetch an ARS-scoped JWT so /ars/** calls after MFA have a valid
                // Authorization header. Pass afterMfa=true + the single-use otpProof
                // from verifyOTP so the dispatcher can verify OTP actually happened —
                // without the proof, afterMfa=true would be trusted blindly.
                await fetchArsTokenForLink(true, resp.resp);

                // Navigate to the main page
                navigate('/newrequest');
                return true;
            } else {
                throw new Error(resp.message || 'Invalid verification code');
            }
        } catch (error) {
            setMFALoading(false);
            setAlertMsg(error.message || 'Invalid verification code');
            setAlertOpen(true);
            return false;
        }
    };

    useEffect(() => {
        validateAccount()
        getFingerprint();
    }, [])

    const getFingerprint = async () => {
        const fp = new Fingerprint();
        const result = fp.get();
        setSessionData(result, 'uniqueDeviceId')
    };

    return (
        <>
            <Grid container justifyContent={'center'} alignItems="center" sx={{ minHeight: 300 }}>
                {!isValid ? (
                    <Box m={3}>Sorry, Your account is not valid...! Please contact the corresponding officer.</Box>
                ) : requiresMFA ? (
                    <MFASecurityPage 
                        onVerifyEmail={handleVerifyEmail} 
                        onVerifyOTP={handleVerifyOTP} 
                        loading={mfaLoading}
                        accountName={accountName}
                    />
                ) : (
                    <ARSSecurityPage 
                        onSubmit={validatePassphrase} 
                        loading={loading}
                        accountName={accountName}
                    />
                )}
            </Grid>

            <Snackbar 
                open={alertOpen} 
                autoHideDuration={6000} 
                onClose={handleAlertClose}  
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
            >
                <Alert onClose={handleAlertClose} severity="error" sx={{ width: '100%' }}>
                    {alertMsg}
                </Alert>
            </Snackbar>
        </>
    )
}

export default ValidateAccount